import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { formatUsd } from '@/components/Shop/ui/format';
import { ShopBuyButton, ShopTitle } from '@/components/Shop/ui/ShopUi';
import { VipIcon } from '@/components/ui/Icons';
import { Picture } from '@/components/ui/Picture/Picture';
import { useVipPlans } from '@/components/Vip/useVipPlans';

import { VipPeriodCard } from './VipPeriodCard';

import css from './styles.module.scss';

import cashPng from '/img/shop/v2/vip/cash.png';
import cashWebp from '/img/shop/v2/vip/cash.webp';
import checkIcon from '/img/shop/v2/vip/check.svg';
import contractsPng from '/img/shop/v2/vip/contracts.png';
import contractsWebp from '/img/shop/v2/vip/contracts.webp';
import customPng from '/img/shop/v2/vip/custom.png';
import customWebp from '/img/shop/v2/vip/custom.webp';
import flashPng from '/img/shop/v2/vip/flash.png';
import flashWebp from '/img/shop/v2/vip/flash.webp';
import noAdsIcon from '/img/shop/v2/vip/no-ads.svg';

const icon = (png: string, webp: string, scale = 1): ReactNode => (
  <div className="relative size-8 shrink-0 overflow-hidden">
    <Picture
      img={png}
      webp={webp}
      alt=""
      className="block size-full"
      style={{ transform: `scale(${scale})` }}
    />
  </div>
);

// Figma "benefits-scroll" of the VIP tab, in its order
const benefits: { icon: () => ReactNode; accent: string; text: string }[] = [
  {
    icon: () => <img src={noAdsIcon} alt="" className="size-8 shrink-0" />,
    accent: 'With no ADS',
    text: 'Bonuses & boosts',
  },
  {
    icon: () => icon(contractsPng, contractsWebp),
    accent: 'Free',
    text: 'All Special contracts',
  },
  {
    icon: () => icon(cashPng, cashWebp),
    accent: '+10%',
    text: 'from Contracts',
  },
  {
    icon: () => icon(customPng, customWebp, 1.0667),
    accent: 'One Tap',
    text: 'production material loading',
  },
  {
    icon: () => <VipIcon className="size-8 shrink-0" />,
    accent: 'Status',
    text: 'in the Arena and in the Office',
  },
  {
    icon: () => icon(flashPng, flashWebp, 1.2522),
    accent: '+15',
    text: 'Charges in Roll Craft',
  },
];

/** "VIP" tab of the shop: benefits and the VIP periods for sale. */
const VipShop: FC = () => {
  const { t } = useTranslation();
  const {
    plans,
    selectedIndex,
    setSelectedIndex,
    selected,
    sales,
    getPriceByDiscount,
    storePrice,
    buy,
    isAppStore,
  } = useVipPlans(2);

  return (
    <div className="w-full flex flex-col px-4">
      <div className="flex flex-col items-center gap-2 pt-4 shrink-0">
        <ShopTitle>{t('Buy VIP')}</ShopTitle>
        <p className={css.subtitle}>
          {t(
            'Get more features, speed up your progress, and enjoy exclusive privileges',
          )}
        </p>
      </div>

      <ul className={cn(css.benefits, 'mt-4')}>
        {benefits.map((benefit) => (
          <li key={benefit.accent} className={css.benefit}>
            <div className="flex items-center gap-2 min-w-0">
              {benefit.icon()}
              <p className={css.benefitText}>
                <span className="font-bold text-gold">{t(benefit.accent)}</span>{' '}
                <span className="font-medium text-white">
                  {t(benefit.text)}
                </span>
              </p>
            </div>
            <img src={checkIcon} alt="" className="size-6 shrink-0" />
          </li>
        ))}
      </ul>

      <div className="flex flex-col pt-7 pb-4 shrink-0">
        <div className={css.periodTitle}>
          <div className={css.line} />
          <span>{t('Choose a period')}</span>
          <div className={cn(css.line, '-scale-x-100')} />
        </div>
        <div className="grid grid-cols-4 gap-1 mt-5">
          {plans.map((plan, i) => (
            <VipPeriodCard
              key={plan.days}
              days={plan.days}
              price={plan.discountPrice}
              oldPrice={getPriceByDiscount(i)}
              sale={sales[i]}
              active={selectedIndex === i}
              appleVipDays={isAppStore ? plan.days : undefined}
              onClick={() => setSelectedIndex(i)}
            />
          ))}
        </div>
        <ShopBuyButton
          className="!h-12 !rounded-[8px]"
          wrapperClassName="mt-4"
          label={`${t('Buy for')} ${storePrice ?? (selected ? formatUsd(selected.discountPrice) : '')}`}
          disabled={!selected}
          onClick={buy}
        />
      </div>
    </div>
  );
};

export { VipShop };
